import {
  DesfaturamentoTipo,
  MensagemTipoAutor,
  NivelSituacaoTarefa,
  TarefaStatus,
  TarefaTipo,
  TarefaVisibilidade,
} from "@workflow/types";
import { JobDTO } from "./job";
import { PessoaDTO } from "./pessoa";
import { ArquivoDTO } from "./arquivo.type";
import { LinkRecursoDTO } from "@/lib/navigation-helpers";
import { ConclusaoDescriptorDTO } from "./conclusao.type";

export interface TarefaDTO {
  id: number;

  tipo: TarefaTipo;

  nome: string;
  descricao: string;

  status: TarefaStatus;
  visibilidade?: TarefaVisibilidade;
  atribuidoA: PessoaDTO;

  job?: JobDTO;
  arquivos?: ArquivoDTO[];
  mensagens?: TarefaMensagemDTO[];
  tarefaObservadores?: TarefaObservadorDTO[];

  origem?: LinkRecursoDTO;
  acao?: LinkRecursoDTO;

  /** O que o tipo da tarefa guarda estruturado (ex.: o contato e a instrução do envio de aniversário). */
  contexto?: Record<string, unknown>;

  /** Como concluir a tarefa. Presente no detalhe (GET /tarefas/:id). */
  conclusao?: ConclusaoDescriptorDTO;

  dataConclusao?: Date;
  dataPrazo?: Date;

  alteradoEm?: Date;
  alteradoPor?: PessoaDTO;
  criadoEm?: Date;
  criadoPor?: PessoaDTO;
}

/** Resposta de `/tarefas/notificacoes`: as duas abas do popover de notificações, já separadas pelo backend. */
export interface TarefaNotificacoesDTO {
  pendentes: TarefaDTO[];
  mensagens: TarefaDTO[];
}

export interface CriarTarefaGenericaPayload {
  nome: string;
  descricao: string;
  visibilidade: TarefaVisibilidade;
  atribuirAId: number;
  jobId: number;
  acao?: LinkRecursoDTO;
  dataPrazo?: Date;
}

/** Edição de tarefa genérica: só a visibilidade pode ser alterada. */
export interface EditarTarefaGenericaPayload {
  visibilidade: TarefaVisibilidade;
}

export interface CriarArquivoJobPayload {
  jobId: number;
  atribuirAId: number;
  dataPrazo?: Date;
}

/**
 * Critério para listar tarefas pendentes (PENDENTE/EM_EXECUCAO) atribuídas ao usuário logado.
 * Espelha o critério do backend; todos os filtros são opcionais e combináveis.
 */
export interface TarefasPendentesAtribuidasCriteria {
  tipos?: TarefaTipo[];
  jobId?: number;
  /** Filtra pelas tarefas cujo contexto referencia esta AP (`contexto.apId`). */
  apId?: number;
  /** Filtra pelas tarefas cujo contexto referencia esta Planilha (`contexto.planilhaId`). */
  planilhaId?: number;
}

export interface TarefaMensagemDTO {
  id: number;

  mensagem: string;
  tipo: MensagemTipoAutor;

  alteradoEm?: Date;
  alteradoPor?: PessoaDTO;
  criadoEm?: Date;
  criadoPor?: PessoaDTO;
}

export interface TarefaVisualizacaoDTO {
  pessoa: { id: number; nome: string };
  papel: "CRIADOR" | "ATRIBUIDO" | "OBSERVADOR";
  visualizadoEm: Date | null;
}

export interface TarefaArquivoDTO {
  id: number;

  arquivo: ArquivoDTO;
  tarefa: TarefaDTO;

  alteradoEm?: Date;
  alteradoPor?: PessoaDTO;
  criadoEm?: Date;
  criadoPor?: PessoaDTO;
}

export interface TarefaObservadorDTO {
  id: number;

  tarefa: TarefaDTO;
  pessoa: PessoaDTO;

  alteradoEm?: Date;
  alteradoPor?: PessoaDTO;
  criadoEm?: Date;
  criadoPor?: PessoaDTO;
}

export interface TarefaResumoTotalDTO {
  totalGeral: number;
  criadasPeloUsuario: number;
  atribuidasAoUsuario: number;
  observadasPeloUsuario: number;
}

/** Fluxo de tarefas do usuário numa janela (últimos N dias): criadas/atribuídas. */
export interface TarefaResumoPeriodoDTO {
  criadas: number;
  atribuidas: number;
  criadasAbertas: number;
  atribuidasAbertas: number;
}

export interface TarefaResumoAdminDTO {
  totalGeral: number;
  totalAbertas: number;
  totalPendentes: number;
  totalEmExecucao: number;
}

/** Contagem de tarefas por status e dia de criação, num período — alimenta o gráfico do painel admin. */
export interface TarefaResumoAdminPorDiaDTO {
  dia: string;
  status: TarefaStatus;
  total: number;
}

/** Contagem de tarefas concluídas por dia (pela data em que a conclusão aconteceu), num período. */
export interface TarefaConcluidasPorDiaDTO {
  dia: string;
  total: number;
}

/** Quantas tarefas abertas cada pessoa tem atribuída — ranking do painel admin. */
export interface TarefaRankingResponsavelDTO {
  pessoa: { id: number; nome: string; ativo: boolean; foto?: string; fotoThumb?: string };
  total: number;
}

/** Alterna o ranking de responsáveis entre tarefas abertas e concluídas. */
export type TarefaRankingModo = "abertas" | "concluidas";

/** Contagem de tarefas criadas no período, agrupadas por tipo. */
export interface TarefaPorTipoDTO {
  tipo: TarefaTipo;
  total: number;
}

/** Tarefa aberta mais atrasada (mesmo cálculo do aviso pessoal de tarefas vencidas). */
export interface TarefaMaisAtrasadaDTO {
  id: number;
  nome: string;
  status: TarefaStatus;
  atribuidoA?: { id: number; nome: string; ativo: boolean; foto?: string; fotoThumb?: string };
  dataPrazo?: Date;
  atrasoDias: number;
}

export interface TarefaResumoAdminPeriodoDTO {
  criadasPorDia: TarefaResumoAdminPorDiaDTO[];
  concluidasPorDia: TarefaConcluidasPorDiaDTO[];
  porTipo: TarefaPorTipoDTO[];
  totalComentarios: number;
  totalDocumentos: number;
}

/** Períodos suportados pelo painel admin de tarefas. */
export const PERIODOS_RESUMO_ADMIN_TAREFAS = [7, 15, 30] as const;
export type PeriodoResumoAdminTarefas = (typeof PERIODOS_RESUMO_ADMIN_TAREFAS)[number];

/** Situação de atraso das tarefas do usuário logado (fonte da verdade no backend). */
export interface SituacaoTarefasDTO {
  tarefasVencidas: number;
  maiorAtrasoDias: number;
  nivel: NivelSituacaoTarefa;
  /** Segundos que o botão de fechar fica travado (definido por nível no backend). */
  segundosParaDesbloquear: number;
}

export interface TarefaSolicitarApPayload {
  planilhaId: number;
  atribuirAId: number;

  observacoes?: string;
  dataPrazo?: Date;
}

export interface TarefaSolicitarAjusteApPayload {
  apId: number;
  atribuirAId: number;

  motivo: string;
  dataPrazo?: Date;
}

export interface TarefaCobrarAprovacaoApPayload {
  apId: number;
  atribuirAId: number;
  planilhaIds: number[];

  observacoes?: string;
  dataPrazo?: Date;
}

export interface TarefaSolicitarDesfaturamentoPayload {
  faturamentoId: number;
  atribuirAId: number;
  tipo: DesfaturamentoTipo;
  motivo: string;

  clienteNome?: string;
  clienteNumero?: string;
  valor?: number;
  dataPrazo?: Date;
}

export interface TarefaSolicitarOrcamentoPreProducaoPayload {
  planilhaId: number;
  atribuirAId: number;

  observacoes?: string;
  dataPrazo?: Date;
}

export interface TarefaQuestionarVersaoPayload {
  versaoId: number;
  atribuirAId: number;

  motivo: string;
  dataPrazo?: Date;
}

export interface TarefaSolicitarAjustePayload {
  planilhaId: number;
  atribuirAId: number;

  motivo: string;
  dataPrazo?: Date;
}

export interface TarefaQuestionarArquivoAprovacaoPayload {
  planilhaId: number;
  versaoId: number;
  arquivoAprovacaoId: number;

  atribuirAId: number;
  motivo: string;
  dataPrazo?: Date;
}

export interface TarefaQuestionarJobPayload {
  jobId: number;
  atribuirAId: number;

  motivo: string;
  dataPrazo?: Date;
}

export interface TarefaQuestionarBriefingPayload {
  jobId: number;
  atribuirAId: number;

  motivo: string;
  dataPrazo?: Date;
}

export interface TarefaQuestionarPaperPayload {
  jobId: number;
  atribuirAId: number;

  motivo: string;
  dataPrazo?: Date;
}
